import React, { useEffect, useMemo, useState } from "react";
import useStyles from "../../../components/common/model/styles";
import Popup from "../../../components/common/model/Popup";
import closeIcon from "../../../assets/closeModel.png";
import checkedIcon from "../../../assets/check-mark.png";
import deleteIcon from "../../../assets/trash.png";
import { DialogContent, DialogContentText, Grid } from "@mui/material";
import Notification from "../../../components/common/notification/Notification";
import Validation from "../../../utils/Validation";
import FormField from "../../../components/common/FormField";
import { useDispatch, useSelector } from "react-redux";
import { setNotificationAction } from "../../../store/reducers/mmymSlice";
import { formElementFields } from "./taskAllFields";
import ResetFormAlert from "../settings/manageReffer/ResetFormAlert";
import { getAllSubUserAction } from "../../../store/reducers/subUserSlice";
import {
  getAllComplaintsAction,
  getComplaintByIdAction,
} from "../../../store/reducers/complaintSlice";
import dayjs from "dayjs";
import { getDefaultTaskListAction } from "../../../store/reducers/reviewSlice";

interface TaskDetail {
  [key: string]: any;
  type: any | null;
  title: any | null;
  description: any | null;
  assigned_to: any | null;
  status: any | null;
  date: any | null;
  notified_emails: any | null;
}

interface ManageTaskProps {
  open: boolean;
  onHide: () => void;
  title: string;
  selectTask: any;
  isSmallScreen?: boolean;
  onSave?: (data: any) => void;
}

const initialTaskState: TaskDetail = {
  task_id: null,
  complaint_id: null,
  complaint_name: null,
  progress_id: null,
  address: null,
  title: null,
  description: null,
  added_by: null,
  assigned_to: null,
  date: new Date().toISOString().split("T")[0], // Default to current date (YYYY-MM-DD)
  start_time: null,
  end_time: null,
  status: "Pending",
  type: "Task General",
  notified_emails: [],
};

const ManageTask = (props: ManageTaskProps) => {
  const classes = useStyles();
  const { selectTask, onHide, open, title, onSave } = props;
  const isEditMode = Boolean(selectTask?.task_id);
  const dispatch = useDispatch();
  const { users } = useSelector((state: any) => state.subUser);
  const { userProfile } = useSelector((state: any) => state.user);
  const { defaultTaskList } = useSelector((state: any) => state.review);
  const { singleComplaint, complaints } = useSelector(
    (state: any) => state?.complaint,
  );
  const [taskDetail, setTaskDetail] = useState<TaskDetail>(initialTaskState);
  const activeComplaintId =
    taskDetail?.complaint_id || selectTask?.complaint_id;

  const prepareFields = (fields: any[], editMode: boolean) => {
    const nextFields = editMode
      ? fields
      : fields.filter((field) => field.id !== "status");

    return nextFields.map((field) => {
      if (field.id === "status") {
        return {
          ...field,
          disabled: !editMode,
        };
      }

      return field;
    });
  };

  useEffect(() => {
    if (activeComplaintId) {
      dispatch(getComplaintByIdAction({ complaint_id: activeComplaintId }));
      dispatch(getDefaultTaskListAction());
    }
  }, [dispatch, activeComplaintId, open]);

  useEffect(() => {
    if (!open) return;

    dispatch(getAllSubUserAction({ status: "ACTIVE" }));
    dispatch(
      getAllComplaintsAction({
        page: 1,
        limit: 200,
        orderBy: "",
        orderIn: "asc",
        status: "Initiated",
      }),
    );
  }, [dispatch, open]);

  const fieldsToRender = useMemo(() => {
    const assignedOptions = (users || []).map((user: any) => ({
      label: user.name,
      id: user.staff_id ?? user?.tenant_id,
    }));

    const complaintOptions = (complaints || []).map((complaint: any) => ({
      label: complaint.title,
      id: String(complaint.complaint_id),
    }));

    const selectedComplaintId =
      taskDetail?.complaint_id ||
      (selectTask?.complaint_id ? String(selectTask.complaint_id) : "");
    const selectedComplaintLabel =
      selectTask?.complaint_name ||
      singleComplaint?.title ||
      singleComplaint?.complaint_name ||
      "";

    if (
      selectedComplaintId &&
      !complaintOptions.some(
        (option: any) => String(option.id) === String(selectedComplaintId),
      )
    ) {
      complaintOptions.unshift({
        id: String(selectedComplaintId),
        label:
          selectedComplaintLabel || `Complaint #${String(selectedComplaintId)}`,
      });
    }

    const addressOptions = (singleComplaint?.addresses || []).map(
      (location: any) => ({
        label: location.street,
        id: String(location.id),
      }),
    );

    const progressOptions = (singleComplaint?.progress || []).map(
      (progress: any) => ({
        label: progress.description,
        id: String(progress.progress_id),
      }),
    );

    const withOptions = formElementFields.map((field) => {
      if (field.id === "assigned_to") {
        return { ...field, options: assignedOptions };
      }
      if (field.id === "address") {
        return { ...field, options: addressOptions };
      }
      if (field.id === "complaint_id") {
        return { ...field, options: complaintOptions };
      }
      if (field.id === "progress_id") {
        return { ...field, options: progressOptions };
      }
      if(field.id === "type"){
        return {
          ...field,
          options: defaultTaskList.map((task: any) => ({
            id: task.name,
            label: task.name,
          })),
        };
      }
      return field;
    });

    return prepareFields(withOptions, isEditMode);
  }, [
    users,
    complaints,
    singleComplaint,
    isEditMode,
    taskDetail?.complaint_id,
    selectTask,
  ]);

  useEffect(() => {
    if (singleComplaint?.complaint_id && userProfile) {
      setTaskDetail((prevState: any) => ({
        ...prevState,
        complaint_id: singleComplaint.complaint_id,
        assigned_to: userProfile?.staff_id ?? userProfile?.tenant_id, // Set default assigned_to to current user
        added_by: userProfile?.staff_id ?? userProfile?.tenant_id, // Set default added_by to current user
        status: prevState?.task_id ? prevState?.status || "Pending" : "Pending", // keep pending on add, preserve existing on edit
        date: dayjs().format("YYYY-MM-DD"), // Set default date to current date
        start_time: dayjs().format("HH:mm"), // Set default start time to current time
        end_time: dayjs().add(1, "hour").format("HH:mm"), // Set default end time to 1 hour from current time
      }));
    }
  }, [singleComplaint, userProfile]);

  const [alertModelShow, setAlertModelShow] = useState(false);
  const [isClickReset, setIsClickReset] = useState(false);

  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
    isErrorOptional,
  ]: any = Validation({
    type: taskDetail?.type,
    title: taskDetail?.title,
    date: taskDetail?.date,
    start_time: taskDetail?.start_time,
    end_time: taskDetail?.end_time,
  });

  const [isActionCalled, setIsActionCalled] = useState(false);

  useEffect(() => {
    if (selectTask) {
      setTaskDetail({
        task_id: selectTask?.task_id || null,
        complaint_id: selectTask?.complaint_id
          ? String(selectTask.complaint_id)
          : null,
        progress_id: selectTask?.progress_id
          ? String(selectTask.progress_id)
          : null,
        address: selectTask?.address ? String(selectTask.address) : null,
        title: selectTask?.title || null,
        description: selectTask?.description || null,
        added_by: selectTask?.added_by || null,
        assigned_to: selectTask?.assigned_to || null,
        date: selectTask?.date || dayjs().format("YYYY-MM-DD"),
        start_time: selectTask?.start_time || dayjs().format("HH:mm"),
        end_time:
          selectTask?.end_time || dayjs().add(1, "hour").format("HH:mm"),
        status: selectTask?.task_id
          ? selectTask?.status || "Pending"
          : "Pending",
        type: selectTask?.type || "Task General",
        notified_emails: Array.isArray(selectTask?.notified_emails)
          ? selectTask.notified_emails
          : typeof selectTask?.notified_emails === "string" &&
              selectTask?.notified_emails
            ? selectTask.notified_emails
                .split(";")
                .map((email: string) => email.trim())
                .filter(Boolean)
            : [],
      });
    }
  }, [selectTask]);

  const hideHandler = () => {
    onHide();
    handleClear();
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };

  const handleClear = () => {
    if (selectTask?.task_id) {
      setTaskDetail({
        task_id: selectTask?.task_id || null,
        complaint_id: selectTask?.complaint_id
          ? String(selectTask.complaint_id)
          : null,
        progress_id: selectTask?.progress_id
          ? String(selectTask.progress_id)
          : null,
        address: selectTask?.address ? String(selectTask.address) : null,
        title: selectTask?.title || null,
        description: selectTask?.description || null,
        added_by: selectTask?.added_by || null,
        assigned_to: selectTask?.assigned_to || null,
        date: selectTask?.date || dayjs().format("YYYY-MM-DD"),
        start_time: selectTask?.start_time || dayjs().format("HH:mm"),
        end_time:
          selectTask?.end_time || dayjs().add(1, "hour").format("HH:mm"),
        status: selectTask?.status || "Pending",
        type: selectTask?.type || "Task General",
        notified_emails: Array.isArray(selectTask?.notified_emails)
          ? selectTask.notified_emails
          : typeof selectTask?.notified_emails === "string" &&
              selectTask?.notified_emails
            ? selectTask.notified_emails
                .split(";")
                .map((email: string) => email.trim())
                .filter(Boolean)
            : [],
      });
    } else {
      setTaskDetail({
        ...initialTaskState,
        complaint_id: selectTask?.complaint_id || null,
        assigned_to: userProfile?.staff_id ?? userProfile?.tenant_id,
        added_by: userProfile?.staff_id ?? userProfile?.tenant_id,
        status: "Pending",
        date: dayjs().format("YYYY-MM-DD"),
        start_time: dayjs().format("HH:mm"),
        end_time: dayjs().add(1, "hour").format("HH:mm"),
      });
    }
    resetAllValidator();
    setIsClickReset(false);
    setAlertModelShow(false);
  };

  const openClearPopup = () => {
    setAlertModelShow(true);
  };

  useEffect(() => {
    if (isClickReset) {
      handleClear();
      setAlertModelShow(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClickReset]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    checkValidator();
    setIsActionCalled(true);
  };

  useEffect(() => {
    if (isActionCalled) {
      if (
        !isError() &&
        !isErrorOptional() &&
        error !== null &&
        isActionCalled
      ) {
        // Convert notified_emails array to string before saving
        const taskDataToSave = {
          ...taskDetail,
          notified_emails:
            taskDetail.notified_emails && taskDetail.notified_emails.length > 0
              ? taskDetail.notified_emails
              : [],
        };
        // Save the task data
        if (onSave) {
          onSave(taskDataToSave);
        }
      }
      setIsActionCalled(false);
    }
  }, [error, isActionCalled, isError, isErrorOptional, onSave, taskDetail]);

  const handleInputChange = (field: string, value: any) => {
    const normalizedValue =
      field === "complaint_id" || field === "progress_id" || field === "address"
        ? value
          ? String(value)
          : ""
        : value;

    setTaskDetail((prevState) => ({
      ...prevState,
      [field]: normalizedValue,
    }));
    resetValidator(field);
  };

  const onBlurAction = (e: any) => {
    checkValidator(e.target.id);
  };

  return (
    <>
      <Popup
        open={open}
        title={title}
        onHide={hideHandler}
        fullWidth={true}
        maxWidth={"md"}
        aria-describedby="alert-dialog-slide-description"
        popupBg="#fff"
        header={
          <>
            <img
              onClick={(e: any) => handleSubmit(e)}
              src={checkedIcon}
              className={classes.checkicons}
              alt="save"
            />
            <img
              onClick={openClearPopup}
              src={deleteIcon}
              className={classes.deleteicons}
              alt="delete"
            />
            <img
              onClick={hideHandler}
              src={closeIcon}
              className={classes.closeicons}
              alt="close"
            />
          </>
        }
      >
        <DialogContent sx={{ padding: "16px 10px !important" }}>
          <DialogContentText>
            <>
              <Notification />
              <Grid
                container
                direction="row"
                spacing={0.5}
                rowGap={0}
                rowSpacing={0}
                sx={{
                  background: "#E5F4F1",
                  padding: "10px !important",
                  marginTop: "10px !important",
                  borderRadius: "5px",
                  border: "1px solid #CFCFCF",
                  overflow: "hidden",
                }}
              >
                {fieldsToRender.map((field, i) => {
                  return (
                    <FormField
                      key={field.id}
                      onPaste={(e) =>
                        field?.id === "task_title" ? "" : e.preventDefault()
                      }
                      onKeyDown={(event: any) => {
                        console.log(
                          "event log-",
                          event,
                          event.target.value + event.key,
                        );
                        if (
                          event.keyCode === 8 ||
                          event.keyCode === 9 ||
                          event.keyCode === 37 ||
                          event.keyCode === 38 ||
                          event.keyCode === 39 ||
                          event.keyCode === 40 ||
                          event.keyCode === 18 ||
                          event.keyCode === 17 ||
                          event.ctrlKey
                        )
                          return event;
                        if (
                          field?.patternAllowed &&
                          !field?.patternAllowed.test(event.key)
                        ) {
                          event.preventDefault();
                        } else if (
                          field?.patternAllowed &&
                          !field?.patternAllowed.test(
                            event.target.value + event.key,
                          )
                        ) {
                          event.preventDefault();
                        }
                      }}
                      {...{
                        ...field,
                        ...{
                          variant: "outlined",
                          inSingleGridForm: true,
                          isNonMarginTop: true,
                          InputProps: {
                            ...(field?.inputProps && field?.inputProps),
                            sx: {
                              height:
                                field?.id === "description" &&
                                taskDetail[field?.id]?.length > 36
                                  ? "auto"
                                  : field?.type === "textarea"
                                    ? "auto"
                                    : "36px",
                              padding:
                                field?.id === "description" ||
                                field?.type === "textarea"
                                  ? "4px"
                                  : "",
                            },
                            autocomplete: "task-new-field",
                          },
                          value: taskDetail ? taskDetail[field.id] : "",
                          onBlur: onBlurAction,
                          onChange: (e: any) =>
                            handleInputChange(field.id, e.target.value),
                          error: error && error[field?.id],
                        },
                      }}
                    />
                  );
                })}
              </Grid>
            </>
          </DialogContentText>
        </DialogContent>
      </Popup>
      {alertModelShow && (
        <ResetFormAlert
          open={alertModelShow}
          title=""
          alertModalShow={alertModelShow}
          setAlertModelShow={setAlertModelShow}
          setIsClickReset={setIsClickReset}
        />
      )}
    </>
  );
};

export default ManageTask;
